import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import MarkEmailUnreadOutlinedIcon from "@mui/icons-material/MarkEmailUnreadOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";

const API = process.env.REACT_APP_API_URL;

const ContactUs = () => {
  const theme = useTheme();
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [openView, setOpenView] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const fetchContacts = async () => {
    setIsLoading(true);
    const loadingToast = toast.loading("Loading contact messages...");
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}contactus/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setContacts(res.data);
      toast.dismiss(loadingToast);
      setIsLoading(false);
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("Failed to fetch contact messages");
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    const loadingToast = toast.loading("Deleting message...");
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}contactus/${selectedContact.id}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.dismiss(loadingToast);
      toast.success("Message deleted successfully");
      setOpenDelete(false);
      setSelectedContact(null);
      fetchContacts();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("Failed to delete message");
    }
  };

  const filteredContacts = contacts.filter((c) =>
    [c.first_name, c.email, c.phone]
      .join(" ")
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    return date.toLocaleString("en-IN", options);
  };

  const ContactCard = ({ contact }) => (
    <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200 hover:shadow-lg transition-shadow duration-200">
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MarkEmailUnreadOutlinedIcon className="text-blue-600 text-lg" />
            <span className="font-semibold text-gray-900">
              {contact.first_name}
            </span>
          </div>
          <span className="text-xs text-gray-500">
            {formatDateTime(contact.created_at)}
          </span>
        </div>

        {/* Contact Details */}
        <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-gray-500">Email:</span>
              <span className="text-gray-900 ml-1 font-medium break-all">
                {contact.email}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Phone:</span>
              <span className="text-gray-900 ml-1 font-medium">
                {contact.phone}
              </span>
            </div>
          </div>
          <div>
            <span className="text-gray-500">Message:</span>
            <div className="mt-1 text-gray-900 line-clamp-2">
              {contact.description}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
          <button
            onClick={() => {
              setSelectedContact(contact);
              setOpenView(true);
            }}
            className="bg-blue-600 text-white px-3 py-2 rounded-lg text-xs hover:bg-blue-700 transition-colors duration-200 shadow-sm flex-1 flex items-center justify-center gap-1"
          >
            <VisibilityIcon className="text-sm" />
            View Details
          </button>
          <button
            onClick={() => {
              setSelectedContact(contact);
              setOpenDelete(true);
            }}
            className="bg-red-600 text-white px-3 py-2 rounded-lg text-xs hover:bg-red-700 transition-colors duration-200 shadow-sm flex-1 flex items-center justify-center gap-1"
          >
            <DeleteForeverIcon className="text-sm" />
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    fetchContacts();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-6">
      <div className="max-w-full mx-auto">
        {/* Search Section */}
        <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 mb-6">
          <div className="flex items-center gap-3">
            <SearchIcon className="text-gray-600" />
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search by name, email, or phone"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
            />
            <button
              onClick={fetchContacts}
              disabled={isLoading}
              className="flex items-center gap-2 bg-gray-100 text-gray-600 p-2 rounded-lg hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Refresh messages"
            >
              <RefreshIcon
                className={`text-sm ${isLoading ? "animate-spin" : ""}`}
              />
            </button>
          </div>
        </div>

        {/* Mobile Cards View */}
        <div className="block md:hidden">
          {filteredContacts.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <div className="flex flex-col items-center gap-2">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                  <MarkEmailUnreadOutlinedIcon className="text-gray-400 text-2xl" />
                </div>
                <p className="text-gray-500 font-medium">No messages found</p>
                <p className="text-gray-400 text-sm">
                  {searchQuery
                    ? "Try adjusting your search terms"
                    : "No contact messages have been submitted yet"}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredContacts.map((contact, index) => (
                <ContactCard key={index} contact={contact} />
              ))}
            </div>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
                <tr>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Name
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Email
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Phone
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Message
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Date
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredContacts.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                          <MarkEmailUnreadOutlinedIcon className="text-gray-400 text-2xl" />
                        </div>
                        <p className="text-gray-500 font-medium">
                          No messages found
                        </p>
                        <p className="text-gray-400 text-sm">
                          {searchQuery
                            ? "Try adjusting your search terms"
                            : "No contact messages have been submitted yet"}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredContacts.map((contact) => (
                    <tr
                      key={contact.id}
                      className="hover:bg-gray-50 transition-colors duration-200"
                    >
                      <td className="px-4 py-4">
                        <div className="font-medium text-gray-900">
                          {contact.first_name}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-gray-600">
                        {contact.email}
                      </td>
                      <td className="px-4 py-4 text-gray-600">
                        {contact.phone}
                      </td>
                      <td className="px-4 py-4 text-gray-600 max-w-xs truncate">
                        {contact.description}
                      </td>
                      <td className="px-4 py-4 text-gray-600 text-sm">
                        {formatDateTime(contact.created_at)}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() => {
                              setSelectedContact(contact);
                              setOpenView(true);
                            }}
                            className="bg-blue-600 text-white px-3 py-2 rounded-lg text-xs hover:bg-blue-700 transition-colors duration-200 shadow-sm flex items-center gap-1"
                          >
                            <VisibilityIcon className="text-sm" />
                            View
                          </button>
                          <button
                            onClick={() => {
                              setSelectedContact(contact);
                              setOpenDelete(true);
                            }}
                            className="bg-red-600 text-white px-3 py-2 rounded-lg text-xs hover:bg-red-700 transition-colors duration-200 shadow-sm flex items-center gap-1"
                          >
                            <DeleteForeverIcon className="text-sm" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* View Modal */}
      <Dialog
        open={openView}
        onClose={() => setOpenView(false)}
        fullWidth
        maxWidth={isSmallMobile ? "xs" : "sm"}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            margin: isSmallMobile ? "16px" : "32px",
          },
        }}
      >
        <DialogTitle className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <MarkEmailUnreadOutlinedIcon className="text-white" />
            </div>
            <Typography variant="h6" className="font-bold">
              Message Details
            </Typography>
          </div>
        </DialogTitle>
        <DialogContent className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm mt-3">
            <div>
              <span className="text-gray-500 font-medium">Name:</span>
              <span className="text-gray-900 ml-2 font-medium">
                {selectedContact?.first_name}
              </span>
            </div>
            <div>
              <span className="text-gray-500 font-medium">Date:</span>
              <span className="text-gray-900 ml-2">
                {selectedContact?.created_at
                  ? formatDateTime(selectedContact.created_at)
                  : "—"}
              </span>
            </div>
          </div>

          <div>
            <span className="text-gray-500 font-medium">Email:</span>
            <span className="text-gray-900 ml-2 font-medium">
              {selectedContact?.email}
            </span>
          </div>

          <div>
            <span className="text-gray-500 font-medium">Phone:</span>
            <span className="text-gray-900 ml-2 font-medium">
              {selectedContact?.phone}
            </span>
          </div>

          <div>
            <span className="text-gray-500 font-medium">Message:</span>
            <div className="mt-2 p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-900">
                {selectedContact?.description}
              </span>
            </div>
          </div>
        </DialogContent>
        <DialogActions className="p-6 pt-0">
          <Button
            onClick={() => setOpenView(false)}
            variant="outlined"
            sx={{
              borderRadius: "12px",
              borderColor: "#d1d5db",
              color: "#374151",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
            }}
            startIcon={<CloseIcon />}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            margin: isSmallMobile ? "16px" : "32px",
          },
        }}
      >
        <DialogTitle className="flex items-center gap-3 text-red-600 p-6">
          <div className="p-2 bg-red-100 rounded-lg">
            <DeleteForeverIcon color="error" />
          </div>
          <Typography variant="h6" className="font-bold">
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent className="p-6 pt-0">
          <Typography variant="body1" className="text-gray-700 leading-relaxed">
            Are you sure you want to delete this message from{" "}
            {selectedContact?.first_name}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions className="p-6 pt-0 gap-3">
          <Button
            onClick={() => setOpenDelete(false)}
            variant="outlined"
            fullWidth={isSmallMobile}
            sx={{
              borderRadius: "12px",
              borderColor: "#d1d5db",
              color: "#374151",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            fullWidth={isSmallMobile}
            className="bg-red-600 hover:bg-red-700"
            sx={{ borderRadius: "12px" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ContactUs;
